import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import LoginIcon from '@mui/icons-material/Login';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import axiosInstance from '../../config/axiosInstance';
import { UserContext } from '../context/UserContext';


const MAX_ROWS = 200;


function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ mt: 3 }}>{children}</Box>;
}


function formatDateTime(value) {
  if (!value) return '-';

  try {
    return new Date(value).toLocaleString('es-UY', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return value;
  }
}


function JsonPreview({ value }) {
  if (!value) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  const content = typeof value === 'string' ? value : JSON.stringify(value, null, 2);

  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        maxWidth: 320,
        maxHeight: 120,
        overflow: 'auto',
        fontSize: 12,
        lineHeight: 1.35,
        borderRadius: 1,
        bgcolor: '#f8fafc',
        border: '1px solid',
        borderColor: 'divider',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {content}
    </Box>
  );
}


export default function AuditPage() {
  const { user } = useContext(UserContext);
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';

  const [tab, setTab] = useState(0);

  const [loginQuery, setLoginQuery] = useState('');
  const [loginRows, setLoginRows] = useState([]);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [auditFilterOptions, setAuditFilterOptions] = useState({
    entity_names: [],
    entity_tables: [],
    operations: [],
    actor_emails: [],
  });
  const [auditFilters, setAuditFilters] = useState({
    q: '',
    entity_name: '',
    entity_table: '',
    operation: '',
    entity_id: '',
    actor_email: '',
    date_from: '',
    date_to: '',
  });

  const fetchLoginLogs = async (query = loginQuery) => {
    setLoginLoading(true);
    setLoginError('');

    try {
      const { data } = await axiosInstance.get('/audit/login_log', {
        params: {
          q: query,
          limit: MAX_ROWS,
        },
      });
      setLoginRows(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      setLoginError(error.response?.data?.error || 'No se pudieron cargar los usuarios logueados.');
    } finally {
      setLoginLoading(false);
    }
  };

  const fetchAuditLogs = async (filters = auditFilters) => {
    setAuditLoading(true);
    setAuditError('');

    try {
      const params = { limit: MAX_ROWS };
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params[key] = value;
        }
      });

      const { data } = await axiosInstance.get('/audit/change_log', { params });
      setAuditRows(Array.isArray(data?.items) ? data.items : []);
      setAuditFilterOptions(data?.filter_options || {
        entity_names: [],
        entity_tables: [],
        operations: [],
        actor_emails: [],
      });
    } catch (error) {
      setAuditError(error.response?.data?.error || 'No se pudieron cargar los logs de cambios.');
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchLoginLogs('');
    fetchAuditLogs({
      q: '',
      entity_name: '',
      entity_table: '',
      operation: '',
      entity_id: '',
      actor_email: '',
      date_from: '',
      date_to: '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const handleAuditFilterChange = (field) => (event) => {
    setAuditFilters((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const resetAuditFilters = () => {
    const cleared = {
      q: '',
      entity_name: '',
      entity_table: '',
      operation: '',
      entity_id: '',
      actor_email: '',
      date_from: '',
      date_to: '',
    };
    setAuditFilters(cleared);
    fetchAuditLogs(cleared);
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Solo `super_admin` puede acceder a la sección de Auditoría.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Auditoría
          </Typography>
          <Typography color="text.secondary">
            Consulta de ingresos al sistema y cambios registrados en base.
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Mostrando hasta {MAX_ROWS} registros por vista
        </Typography>
      </Stack>

      <Paper sx={{ mt: 3, p: 2 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" allowScrollButtonsMobile>
          <Tab icon={<LoginIcon />} iconPosition="start" label="Intentos de login" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label="Logs de cambios" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Buscar por usuario, email, estado o motivo"
              value={loginQuery}
              onChange={(event) => setLoginQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  fetchLoginLogs();
                }
              }}
            />
            <Button variant="contained" startIcon={<SearchIcon />} onClick={() => fetchLoginLogs()}>
              Buscar
            </Button>
          </Stack>

          {loginError && <Alert severity="error" sx={{ mb: 2 }}>{loginError}</Alert>}

          <TableContainer sx={{ maxHeight: 560 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>ID usuario</TableCell>
                  <TableCell>Fecha y hora</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loginLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : loginRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No se encontraron intentos de login.
                    </TableCell>
                  </TableRow>
                ) : (
                  loginRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.result === 'failed' ? 'Fallido' : 'Exitoso'}</TableCell>
                      <TableCell>{row.failure_reason || '-'}</TableCell>
                      <TableCell>{row.user_name || '-'}</TableCell>
                      <TableCell>{row.user_email || '-'}</TableCell>
                      <TableCell>{row.user_id ?? '-'}</TableCell>
                      <TableCell>{formatDateTime(row.logged_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Stack spacing={2} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Filtro general"
              helperText="Busca en entidad, tabla técnica, operación, actor, request y contenido del cambio."
              value={auditFilters.q}
              onChange={handleAuditFilterChange('q')}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                fullWidth
                label="Entidad"
                value={auditFilters.entity_name}
                onChange={handleAuditFilterChange('entity_name')}
              >
                <MenuItem value="">Todas</MenuItem>
                {auditFilterOptions.entity_names.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                label="Tabla técnica"
                value={auditFilters.entity_table}
                onChange={handleAuditFilterChange('entity_table')}
              >
                <MenuItem value="">Todas</MenuItem>
                {auditFilterOptions.entity_tables.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                label="Operación"
                value={auditFilters.operation}
                onChange={handleAuditFilterChange('operation')}
              >
                <MenuItem value="">Todas</MenuItem>
                {auditFilterOptions.operations.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                label="ID entidad"
                value={auditFilters.entity_id}
                onChange={handleAuditFilterChange('entity_id')}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                fullWidth
                label="Actor"
                value={auditFilters.actor_email}
                onChange={handleAuditFilterChange('actor_email')}
              >
                <MenuItem value="">Todos</MenuItem>
                {auditFilterOptions.actor_emails.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                label="Desde"
                type="date"
                value={auditFilters.date_from}
                onChange={handleAuditFilterChange('date_from')}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Hasta"
                type="date"
                value={auditFilters.date_to}
                onChange={handleAuditFilterChange('date_to')}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Button variant="contained" startIcon={<SearchIcon />} onClick={() => fetchAuditLogs()}>
                Aplicar filtros
              </Button>
              <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetAuditFilters}>
                Limpiar
              </Button>
            </Stack>
          </Stack>

          {auditError && <Alert severity="error" sx={{ mb: 2 }}>{auditError}</Alert>}

          <TableContainer sx={{ maxHeight: 640 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Fecha y hora</TableCell>
                  <TableCell>Entidad</TableCell>
                  <TableCell>Tabla</TableCell>
                  <TableCell>Operación</TableCell>
                  <TableCell>ID entidad</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Request</TableCell>
                  <TableCell>Datos anteriores</TableCell>
                  <TableCell>Datos nuevos</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLoading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : auditRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No se encontraron cambios con esos filtros.
                    </TableCell>
                  </TableRow>
                ) : (
                  auditRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{formatDateTime(row.occurred_at)}</TableCell>
                      <TableCell>{row.entity_name}</TableCell>
                      <TableCell>{row.entity_table}</TableCell>
                      <TableCell>{row.operation}</TableCell>
                      <TableCell>{row.entity_id}</TableCell>
                      <TableCell>{row.actor_email || row.actor_user_id || '-'}</TableCell>
                      <TableCell>{row.request_method && row.request_path ? `${row.request_method} ${row.request_path}` : row.request_path || '-'}</TableCell>
                      <TableCell><JsonPreview value={row.before_data} /></TableCell>
                      <TableCell><JsonPreview value={row.after_data} /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>
    </Box>
  );
}
